import some from "lodash/some";
import { FC, PropsWithChildren, useMemo } from "react";
import useInternalUser from "~@/hooks/useInternalUser";
import { PERMISSIONS, SCOPES } from "~@/permission-maps";

type PermissionsGateProps = {
  scopes?: SCOPES[];
  RenderError?: () => JSX.Element;
  availableToRoot?: boolean;
};

type HasPermission = (props: {
  permissions: SCOPES[];
  scopes: SCOPES[];
}) => boolean;

const hasPermission: HasPermission = ({ permissions, scopes }) => {
  const scopesMap: Partial<Record<SCOPES, boolean>> = {};

  scopes.forEach((scope) => {
    scopesMap[scope] = true;
  });

  return some(permissions, (permission) => scopesMap[permission]);
};

export const usePermissionsGate = (
  scopes: PermissionsGateProps["scopes"] = []
) => {
  const { internalUser } = useInternalUser();

  const permissionGranted = useMemo(
    () =>
      hasPermission({
        permissions:
          PERMISSIONS[internalUser?.role?.roleTechnicalName ?? "unknown"],
        scopes,
      }),
    [internalUser, scopes]
  );

  return permissionGranted;
};

const PermissionsGate: FC<PropsWithChildren<PermissionsGateProps>> = ({
  children,
  scopes = [],
  RenderError,
  availableToRoot,
}) => {
  const permissionGranted = usePermissionsGate(scopes);

  if (availableToRoot) return <>{children}</>;

  if (!permissionGranted) return RenderError ? <RenderError /> : null;

  return <>{children}</>;
};

export default PermissionsGate;
